import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import './index.scss';
import { useDocReader, useDocReaderDispatch } from '../../store/docReaderContext';
import { TocPage } from '../docPage';
import PdfList from '../pdfList';
import { PdfOutline } from '../../services/useUpdateDocReaderOutline';
import { isMobile } from '@umu/umu-ua';
import { childPostControl } from '@umu/umu-iframe/src/session/postMessage/childPostControl';

interface IPropsOutlineItem {
    outline: PdfOutline;
    nextOutline: PdfOutline;
    indentation?: number;
}
function OutlineItem({ outline, nextOutline, indentation = 1 }: IPropsOutlineItem) {
    const dispatch = useDocReaderDispatch();
    const [state] = useDocReader();
    const [extract, setExtract] = useState(false);
    const itemRef = useRef<HTMLDivElement>(null);
    const isInCurrentOutline = useMemo(
        () => outline.pageNum <= state.displayPageNum && nextOutline.pageNum > state.displayPageNum,
        [state.displayPageNum, outline.pageNum, outline],
    );
    useEffect(() => {
        if (isInCurrentOutline) {
            setExtract(true);
            if (outline.items.length === 0) {
                itemRef.current?.scrollIntoView({
                    block: 'center',
                });
            }
        }
    }, [state.displayPageNum, isInCurrentOutline]);
    return (
        <>
            <div
                ref={itemRef}
                className={classNames({
                    'outline-item': true,
                    'selected': isInCurrentOutline,
                })}
                style={{
                    paddingLeft: `${indentation * 20}px`,
                }}
                onClick={() => {
                    dispatch({ type: 'pageJump', pageNum: outline.pageNum });
                    setExtract(!extract);
                }}
            >
                <div className="current-item">
                    <div
                        className={classNames({
                            'outline-item-extract-btn': true,
                            'no-child': outline.items.length === 0,
                            'extract': extract,
                        })}
                        onClick={() => setExtract(!extract)}
                    ></div>
                    <div className="outline-item-title">{outline.title}</div>
                </div>
            </div>
            {outline.items.length > 0 &&
                extract &&
                outline.items.map((item, index, arr) => {
                    return (
                        <OutlineItem
                            key={index}
                            outline={item}
                            nextOutline={arr?.[index + 1] || nextOutline}
                            indentation={indentation + 1}
                        />
                    );
                })}
        </>
    );
}

export default function OutlineSidebar() {
    const [mode, setMode] = useState<'img' | 'toc'>('img');
    const [state] = useDocReader();
    const dispatch = useDocReaderDispatch();
    const { docReaderProps } = state;
    useEffect(() => {
        if (isMobile || state.screenState !== 'full' || state.pdfOutlines.length === 0) {
            childPostControl.sendSideBarFold(true, true);
        } else {
            childPostControl.sendSideBarFold(false, true);
        }
    }, [state.screenState, state.pdfOutlines]);
    if (isMobile || state.screenState !== 'full' || state.pdfOutlines.length === 0) {
        return <></>;
    }
    return (
        <>
            <div
                className="outline-sidebar"
                style={{
                    width: state.showOutlines ? '' : '0px',
                }}
            >
                <div className="outline-sidebar-header">
                    <div
                        className={classNames({
                            'outline-sidebar-header-mode-btn-group': true,
                            'img-mode': mode === 'img',
                            'toc-mode': mode === 'toc',
                        })}
                    >
                        <div className="outline-sidebar-header-mode-btn" onClick={() => setMode('img')}>
                            <div className="btn-img"></div>
                        </div>
                        <div className="outline-sidebar-header-mode-btn" onClick={() => setMode('toc')}>
                            <div className="btn-toc"></div>
                        </div>
                    </div>
                    <div
                        className="outline-sidebar-header-hide-btn"
                        onClick={() => dispatch({ type: 'outlineSidebarHide' })}
                    />
                </div>
                <div className="outline-sidebar-content">
                    <div
                        className={classNames({
                            'outline-sidebar-content-img': true,
                            'hidden': mode !== 'img',
                        })}
                    >
                        <PdfList isPdfOutline>
                            {Array.from({ length: state.totalPages }).map((_, index) => {
                                return <TocPage pageNum={index + 1} key={index} />;
                            })}
                        </PdfList>
                    </div>
                    <div
                        className={classNames({
                            'outline-sidebar-content-toc': true,
                            'hidden': mode !== 'toc',
                        })}
                    >
                        <div className="outline-sidebar-content-toc-title">{docReaderProps.documentTitle}</div>
                        <div className="outline-sidebar-content-toc-content">
                            {state.pdfOutlines.map((outline, index, arr) => {
                                return (
                                    <OutlineItem
                                        key={index}
                                        outline={outline}
                                        nextOutline={arr?.[index + 1] || { items: [], pageNum: Infinity }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
            {!state.showOutlines && (
                <div
                    className="outline-sidebar-header-show-btn"
                    onClick={() => dispatch({ type: 'outlineSidebarShow' })}
                />
            )}
        </>
    );
}
